import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause } from "lucide-react";
import { SectionHeading } from "./SectionHeading";

export default function ServicesShowreel({ video, poster, caption }) {
    const ref = useRef(null);
    const [playing, setPlaying] = useState(true);
    const [failed, setFailed] = useState(false);

    const toggle = async () => {
        const v = ref.current;
        if (!v || failed) return;
        try {
            if (v.paused) {
                await v.play();
                setPlaying(true);
            } else {
                v.pause();
                setPlaying(false);
            }
        } catch {
            // Autoplay / source error — swallow
        }
    };

    return (
        <section
            data-testid="home-services-showreel"
            className="relative py-20 md:py-24 border-t border-white/5 overflow-hidden"
        >
            <div className="absolute inset-0 bg-radial-fade opacity-60 pointer-events-none" />
            <div className="relative max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                <div className="lg:col-span-5">
                    <SectionHeading
                        eyebrow="Services in motion"
                        title="See how we build."
                        subtitle="A 30 second glimpse into the rhythm of our global delivery AI pipelines, secure cloud, and production platforms shipping every week."
                    />
                    <ul className="mt-8 space-y-3">
                        {[
                            "Outcome-based engagement models",
                            "Senior practice-led delivery",
                            "Globally distributed, always-on teams",
                        ].map((t) => (
                            <li
                                key={t}
                                className="flex items-center gap-3 text-[14.5px] text-zinc-300"
                            >
                                <span className="h-1.5 w-1.5 rounded-full bg-brand-green" />
                                {t}
                            </li>
                        ))}
                    </ul>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="lg:col-span-7 relative rounded-3xl overflow-hidden border border-white/10 glass-strong aspect-video"
                    data-testid="home-showreel-frame"
                >
                    {!failed ? (
                        <video
                            ref={ref}
                            src={video}
                            poster={poster}
                            autoPlay
                            loop
                            muted
                            playsInline
                            preload="metadata"
                            onError={() => setFailed(true)}
                            className="absolute inset-0 h-full w-full object-cover opacity-90"
                        />
                    ) : (
                        <img
                            src={poster}
                            alt="Services showreel"
                            className="absolute inset-0 h-full w-full object-cover opacity-80"
                        />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-br from-ink-950/40 via-transparent to-ink-950/70 pointer-events-none" />

                    <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-4">
                        <div className="glass rounded-xl border border-white/10 px-4 py-2.5 text-[12.5px] text-zinc-200 max-w-md">
                            {caption}
                        </div>
                        <button
                            type="button"
                            onClick={toggle}
                            data-testid="showreel-toggle"
                            disabled={failed}
                            className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-brand-linear text-white hover:shadow-glow-brand transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label={playing ? "Pause video" : "Play video"}
                        >
                            {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                        </button>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
